import React from 'react';
import SystemHealthMonitor from '@/components/admin/SystemHealthMonitor';
import { GenerationHealthDashboard } from '@/components/admin/GenerationHealthDashboard';
import { DatabaseIntegrityMonitor } from '@/components/admin/DatabaseIntegrityMonitor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, Database, Zap, AlertTriangle, Shield, Archive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { pauseProblematicCrons } from '@/services/cronJobMonitoring';
import { toast } from 'sonner';

const SystemHealthDashboard: React.FC = () => {
  // Auto-pause problematic crons on first load
  React.useEffect(() => {
    const checkAndPauseCrons = async () => {
      try {
        const hasRun = localStorage.getItem('cron_cleanup_completed');
        if (!hasRun) {
          await pauseProblematicCrons();
          localStorage.setItem('cron_cleanup_completed', 'true');
          toast.success('Problematic cron jobs automatically paused');
        }
      } catch (error) {
        console.error('Failed to auto-pause crons:', error);
      }
    };
    
    checkAndPauseCrons();
  }, []);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Activity className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">System Health Dashboard</h1>
          <p className="text-muted-foreground">Monitor system performance and identify issues</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">
            <Activity className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="generation">
            <Shield className="h-4 w-4 mr-2" />
            Generation Health
          </TabsTrigger>
          <TabsTrigger value="database">
            <Database className="h-4 w-4 mr-2" />
            Database
          </TabsTrigger>
          <TabsTrigger value="functions">
            <Zap className="h-4 w-4 mr-2" />
            Edge Functions
          </TabsTrigger>
          <TabsTrigger value="errors">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Error Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <SystemHealthMonitor />
          <DatabaseIntegrityMonitor />
        </TabsContent>

        <TabsContent value="generation" className="space-y-4">
          <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
            <Archive className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <strong>Content Status:</strong> Database shows 97.98% quiz coverage (704/707) and 99.15% lab coverage (701/707). 
              Content generation is nearly complete.
            </AlertDescription>
          </Alert>
          
          <GenerationHealthDashboard />
        </TabsContent>

        <TabsContent value="database" className="space-y-4">
          <DatabaseIntegrityMonitor />
        </TabsContent>

        <TabsContent value="functions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Jobs & Automation</CardTitle>
              <CardDescription>
                Autonomous cron systems have been archived. Content generation is complete.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <Archive className="h-4 w-4" />
                <AlertDescription>
                  Cron job monitoring has been archived as content generation is complete.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors">
          <div className="text-center p-8 text-muted-foreground">
            Detailed error logs coming soon
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemHealthDashboard;